import React, { useEffect, useState } from 'react';

import RecordBadge from '../common/RecordBadge';
import { DUPLICATE } from '../icons';
import { normalizeId } from '../../utils/ids';
import { getItemForCopy } from '../../services/itemCopies';
import { useCurrentUser } from '../../contexts/CurrentUserContext';

/**
 * WHERE A COPY CAME FROM (KPR-1024; keepr-api docs/SCHEMA.md § Copies). A
 * record made with Copy to… carries `copiedFrom: { collection_id, item_id,
 * standIn }`. The ids may name a record THIS reader cannot open — the copier
 * could read it, a grant may since have gone, the original been deleted, or
 * the copy be shared with someone who never could — so the line links to the
 * original only when the API said it is readable (`readable`, with the
 * original's `collection`), and otherwise says so in words, never a link:
 *
 *   ⧉ Copied from [Gran's recipes]                 (a link to the original)
 *   ⧉ Copied from a record you can't open
 *   · shown instead of the original                (standIn, the full line, to the copier:
 *                                                    it is their choice, for their own lists)
 *
 * A read that did not describe it (a List's row) is asked once, by the item's
 * own GET; until it answers — or if it fails — the line says only "Copied
 * from another collection". `compact` is a list row's form: the words and the
 * badge, nothing else. Renders nothing for a record that is not a copy.
 */
export function useCopiedFrom(item) {
	const stored = item?.copiedFrom && typeof item.copiedFrom === 'object' ? item.copiedFrom : null;
	const described = Boolean(stored && typeof stored.readable === 'boolean');
	const itemId = normalizeId(item?._id);
	const [fetched, setFetched] = useState(null);
	useEffect(() => {
		setFetched(null);
		if (!stored || described || !itemId) return undefined;
		let active = true;
		getItemForCopy(itemId)
			.then((data) => { if (active && data?.copiedFrom && typeof data.copiedFrom.readable === 'boolean') setFetched(data.copiedFrom); })
			.catch(() => {});
		return () => { active = false; };
	}, [itemId, Boolean(stored), described]); // eslint-disable-line react-hooks/exhaustive-deps
	if (!stored) return null;
	return described ? stored : (fetched ? { ...fetched, standIn: stored.standIn === true } : stored);
}

export default function CopiedFromNote({ item, compact = false, className = '' }) {
	const copiedFrom = useCopiedFrom(item);
	const { user } = useCurrentUser();
	if (!copiedFrom) return null;
	const own = Boolean(user && normalizeId(item?.owner) && normalizeId(item.owner) === normalizeId(user._id));
	const known = typeof copiedFrom.readable === 'boolean';
	const collection = copiedFrom.readable && copiedFrom.collection ? copiedFrom.collection : null;
	const originalId = normalizeId(copiedFrom.item_id);
	const to = collection && originalId ? `/collections/${normalizeId(collection._id)}/items/${originalId}` : null;
	const stop = event => event.stopPropagation();

	let where;
	if (collection) {
		where = (
			<RecordBadge
				kind="collection"
				record={collection}
				title={collection.name || 'Another collection'}
				size="xs"
				to={to}
				tooltip={`Open the original in ${collection.name || 'its collection'}`}
			/>
		);
	} else if (known && !copiedFrom.readable) {
		where = <span className="copied-from__unreadable">a record you can&apos;t open</span>;
	} else {
		where = <span>another collection</span>;
	}

	const classes = ['item-source', 'copied-from', compact ? 'copied-from--compact' : '', className].filter(Boolean).join(' ');
	return (
		<div className={classes} data-testid="copied-from" onClick={stop} onKeyDown={stop} role="presentation">
			<DUPLICATE aria-hidden="true" className="item-source__icon" />
			<span className="copied-from__words">Copied from</span>
			{where}
			{!compact && own && copiedFrom.standIn ? (
				<>
					<span className="item-source__sep" aria-hidden="true"> · </span>
					<span className="copied-from__stand-in">shown instead of the original</span>
				</>
			) : null}
		</div>
	);
}
